/**
 * JRS collection filters (design option 1, 2026-09-26)
 *
 * Markup: snippets/jrs-filters-toolbar.liquid (Filter button + quick-filter rail
 * with desktop dropdowns) and snippets/jrs-filters-sheet.liquid (the sheet).
 *
 * How it works:
 * - The sheet's form is the source of truth. Dropdown inputs mirror sheet inputs
 *   with the same name + value; a change on either side is copied to the other.
 * - While the shopper picks, the pending count is read from a Section Rendering
 *   API response (?section_id=) of the pending URL, and every "Show N products"
 *   label updates before they commit.
 * - Applying navigates to the filtered collection URL (a full page load). That
 *   keeps sort (sort.js), load-more pagination (collection-pagination.js),
 *   card swatches and the VAT toggle working exactly as they do on first load,
 *   instead of re-initialising each of them after an in-place grid swap.
 * - Filter params are Search & Discovery's own (filter.v.*); sort_by is kept,
 *   page is dropped so a new selection starts at page 1.
 *
 * Analytics (2026-10-03, for the 30-day read of whether filters earn their place):
 * events are sent through window.jrsTrackEvent (assets/jrs-tracking.js) with
 * transport 'datalayer': dataLayer push only, no gtag call, GTM as the single sender.
 * No GTM tag matches jrs_filter_* yet (GTM-CONTAINER-MAP.md, export 2026-09-15):
 * until one is added, these events are visible in the dataLayer only, not in GA4.
 * apply and clear_all are followed by navigation: GTM's eventCallback
 * (eventTimeout 400) is attached to the push, and the page is left after 400ms at most.
 *   jrs_filter_open          entry: filter_button | chip | dropdown; filter_group
 *   jrs_filter_apply         surface: sheet | dropdown; filters; filter_count; result_count
 *   jrs_filter_zero_results  filters (a selection that matched nothing, once each)
 *   jrs_filter_close         changed: whether picks were left unapplied
 *   jrs_filter_clear_all     filter_count cleared
 * Every event carries collection_handle and active_filters (filters already in the URL).
 * The applied filters also stay visible in page_view page_location (filter.v.* params).
 */
(function () {
  'use strict';

  var DESKTOP_QUERY = window.matchMedia('(min-width: 768px)');

  function JrsFilters(sheet) {
    this.sheet = sheet;
    this.form = sheet.querySelector('[data-jf-form]');
    this.rail = document.querySelector('[data-jf-rail]');
    this.sectionId = (document.getElementById('collection-page') || {}).dataset
      ? document.getElementById('collection-page').dataset.id
      : null;
    this.opener = null;
    this.countTimer = null;
    this.countController = null;
    this.lastCount = null;      // last count shown on the apply buttons
    this.changed = false;       // a pick was made since the sheet/dropdown opened
    this.zeroSeen = {};         // selections already reported as zero results
    this.collectionHandle = (document.querySelector('[data-collection-handle]') || { getAttribute: function () { return ''; } })
      .getAttribute('data-collection-handle') || '';
    this.bind();
  }

  // Filters in the URL now, e.g. "product_size:M|choose_colors:#000080" (GA4 caps values at 100 chars)
  function summarise(pairs) {
    return pairs.map(function (p) {
      return p[0].split('.').pop() + ':' + p[1];
    }).join('|').slice(0, 100);
  }

  JrsFilters.prototype.activeInUrl = function () {
    var pairs = [];
    new URLSearchParams(window.location.search).forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) pairs.push([key, value]);
    });
    return pairs;
  };

  JrsFilters.prototype.pending = function () {
    var pairs = [];
    this.form.querySelectorAll('.jf-input:checked').forEach(function (input) {
      pairs.push([input.name, input.value]);
    });
    return pairs;
  };

  // Sent once via GTM (dataLayer only); callback, when given, is run as GTM's eventCallback
  JrsFilters.prototype.track = function (name, params, callback) {
    if (typeof window.jrsTrackEvent !== 'function') return false;
    // Keys from earlier pushes persist in GTM's data model: every key the "GA4 - Collection Filters"
    // tag reads is cleared first, so an event carries only its own params
    var payload = Object.assign({
      entry: undefined, filter_group: undefined, surface: undefined, filters: undefined,
      filter_count: undefined, result_count: undefined, changed: undefined,
      page_from: undefined, page_to: undefined, sort_option: undefined,
      collection_handle: this.collectionHandle,
      active_filters: summarise(this.activeInUrl())
    }, params || {});
    window.jrsTrackEvent(name, payload, { transport: 'datalayer', eventCallback: callback });
    return true;
  };

  JrsFilters.prototype.bind = function () {
    var self = this;

    // Open the sheet: Filter / All filters button
    document.querySelectorAll('[data-jf-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.openSheet(null, btn);
        self.track('filter_open', { entry: 'filter_button', filter_group: '' });
      });
    });

    // Quick-filter chips: sheet on mobile, dropdown on desktop
    document.querySelectorAll('[data-jf-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var group = chip.getAttribute('data-jf-chip');
        if (DESKTOP_QUERY.matches) {
          if (self.togglePop(chip)) self.track('filter_open', { entry: 'dropdown', filter_group: group });
        } else {
          self.openSheet(group, chip);
          self.track('filter_open', { entry: 'chip', filter_group: group });
        }
      });
    });

    // Close controls (scrim, close button)
    this.sheet.querySelectorAll('[data-jf-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.closeSheet();
      });
    });

    // Apply buttons (sheet CTA, each dropdown)
    document.querySelectorAll('[data-jf-apply]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.apply(btn.closest('[data-jf-pop]') ? 'dropdown' : 'sheet');
      });
    });

    // "Clear all" links: navigation held for GTM's eventCallback, 400ms at most.
    // New-tab clicks (ctrl/cmd/shift/middle) are left to the browser default.
    document.querySelectorAll('.jf-clear').forEach(function (link) {
      link.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        var href = link.href;
        var done = false;
        function go() {
          if (done) return;
          done = true;
          window.location.href = href;
        }
        e.preventDefault();
        var sent = self.track('filter_clear_all', { filter_count: self.activeInUrl().length }, go);
        setTimeout(go, sent ? 400 : 0);
      });
    });

    // "Show all 54" on long lists
    document.querySelectorAll('[data-jf-more]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var list = btn.previousElementSibling;
        var open = list.classList.toggle('jf-values--all');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? 'Show fewer' : btn.getAttribute('data-label-more') || btn.textContent;
      });
      btn.setAttribute('data-label-more', btn.textContent);
    });

    // Any option change, in the sheet or a dropdown
    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input.classList || !input.classList.contains('jf-input')) return;
      self.mirror(input);
      self.changed = true;
      self.scheduleCount();
    });

    // Esc closes the sheet or an open dropdown
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (!self.sheet.hidden) {
        self.closeSheet();
      } else {
        self.closePops(true);
      }
    });

    // Click outside closes a dropdown
    document.addEventListener('click', function (event) {
      if (!event.target.closest('[data-jf-dd]')) self.closePops(false);
    });

    // Keep focus inside the open sheet
    this.sheet.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      var focusable = self.focusables();
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Leaving desktop width with a dropdown open: close it
    DESKTOP_QUERY.addEventListener('change', function () {
      self.closePops(false);
    });
  };

  JrsFilters.prototype.focusables = function () {
    return Array.prototype.filter.call(
      this.sheet.querySelectorAll('button, a[href], summary, input:not([disabled])'),
      function (el) {
        return el.offsetParent !== null;
      }
    );
  };

  JrsFilters.prototype.openSheet = function (groupId, opener) {
    this.closePops(false);
    this.opener = opener || null;
    this.changed = false;
    this.sheet.hidden = false;
    document.documentElement.classList.add('jf-lock');

    var target = null;
    if (groupId) {
      target = this.sheet.querySelector('[data-jf-group="' + groupId + '"]');
      if (target) target.open = true;
    }

    // Wait a frame so the panel has layout before scrolling / focusing
    var self = this;
    window.requestAnimationFrame(function () {
      if (target) {
        var body = self.sheet.querySelector('.jf-sheet__body');
        body.scrollTop = target.offsetTop - body.offsetTop;
        var summary = target.querySelector('summary');
        if (summary) summary.focus();
      } else {
        var close = self.sheet.querySelector('.jf-sheet__close');
        if (close) close.focus();
      }
    });
  };

  JrsFilters.prototype.closeSheet = function () {
    if (this.sheet.hidden) return;
    this.track('filter_close', { surface: 'sheet', changed: this.changed });
    this.sheet.hidden = true;
    document.documentElement.classList.remove('jf-lock');
    if (this.opener) this.opener.focus();
  };

  JrsFilters.prototype.togglePop = function (chip) {
    var pop = document.getElementById(chip.getAttribute('aria-controls'));
    if (!pop) return;
    var willOpen = pop.hidden;
    this.closePops(false);
    if (willOpen) {
      this.changed = false;
      pop.hidden = false;
      chip.setAttribute('aria-expanded', 'true');
      var firstInput = pop.querySelector('input:not([disabled])');
      if (firstInput) firstInput.focus();
    }
    return willOpen;
  };

  JrsFilters.prototype.closePops = function (restoreFocus) {
    document.querySelectorAll('[data-jf-pop]').forEach(function (pop) {
      if (pop.hidden) return;
      pop.hidden = true;
      var chip = document.querySelector('[aria-controls="' + pop.id + '"]');
      if (chip) {
        chip.setAttribute('aria-expanded', 'false');
        if (restoreFocus) chip.focus();
      }
    });
  };

  // Copy a checkbox state to every other input with the same name + value
  JrsFilters.prototype.mirror = function (source) {
    document.querySelectorAll('.jf-input').forEach(function (input) {
      if (input !== source && input.name === source.name && input.value === source.value) {
        input.checked = source.checked;
      }
    });
  };

  // Filtered URL for the sheet's current selection
  JrsFilters.prototype.buildParams = function () {
    var params = new URLSearchParams(window.location.search);
    // Keys are copied first, then deleted: deleting while iterating skips entries
    Array.from(params.keys()).forEach(function (key) {
      if (key.indexOf('filter.') === 0 || key === 'page') params.delete(key);
    });
    this.form.querySelectorAll('.jf-input:checked').forEach(function (input) {
      params.append(input.name, input.value);
    });
    return params;
  };

  JrsFilters.prototype.apply = function (surface) {
    var query = this.buildParams().toString();
    var url = window.location.pathname + (query ? '?' + query : '');
    var pairs = this.pending();
    var done = false;
    function go() {
      if (done) return;
      done = true;
      window.location.href = url;
    }
    // Navigate on GTM's eventCallback, or after 400ms without one
    var sent = this.track('filter_apply', {
      surface: surface || 'sheet',
      filters: summarise(pairs),
      filter_count: pairs.length,
      result_count: this.lastCount === null ? -1 : this.lastCount
    }, go);
    setTimeout(go, sent ? 400 : 0);
  };

  JrsFilters.prototype.scheduleCount = function () {
    var self = this;
    clearTimeout(this.countTimer);
    this.setCountLabel(null);
    this.countTimer = setTimeout(function () {
      self.fetchCount();
    }, 250);
  };

  JrsFilters.prototype.fetchCount = function () {
    if (!this.sectionId) return;
    var self = this;
    if (this.countController) this.countController.abort();
    this.countController = new AbortController();

    var params = this.buildParams();
    params.set('section_id', this.sectionId);

    fetch(window.location.pathname + '?' + params.toString(), { signal: this.countController.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('[data-jf-results]');
        // Zero results renders the empty-state branch, which has no sheet
        self.setCountLabel(source ? parseInt(source.getAttribute('data-jf-results'), 10) : 0);
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        // Count unknown: let the shopper apply anyway
        self.setCountLabel(-1);
      });
  };

  // null = loading, -1 = unknown, otherwise the product count
  JrsFilters.prototype.setCountLabel = function (count) {
    if (count !== null) this.lastCount = count;
    var text;
    var disabled = false;
    if (count === null) {
      text = 'Updating…';
    } else if (count === -1) {
      text = 'Show products';
    } else if (count === 0) {
      text = 'No matches. Remove a filter';
      disabled = true;
      var key = summarise(this.pending());
      if (!this.zeroSeen[key]) {
        this.zeroSeen[key] = true;
        this.track('filter_zero_results', { filters: key, filter_count: this.pending().length });
      }
    } else {
      text = 'Show ' + count + (count === 1 ? ' product' : ' products');
    }
    document.querySelectorAll('[data-jf-apply]').forEach(function (btn) {
      var label = btn.querySelector('[data-jf-count-label]');
      if (label) label.textContent = text;
      btn.disabled = disabled;
      btn.classList.toggle('is-loading', count === null);
    });
  };

  function init() {
    var sheet = document.querySelector('[data-jf-sheet]');
    if (sheet && !sheet.jrsFilters) sheet.jrsFilters = new JrsFilters(sheet);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
